import { useEffect, useMemo, useRef, useState } from "react";
import {
    BookmarkX,
    Brush,
    Globe,
    HelpCircle,
    History as HistoryIcon,
    MessagesSquare,
    Pencil,
    Star,
} from "lucide-react";

// SlashPalette — keyboard-navigable command picker anchored above the
// composer. Opens when the draft starts with `/`; the composer passes the
// filter (text after `/` up to whitespace) so the palette narrows.
//
// 8 built-in commands (legacy parity):
//   /fav  /unfav  /rename  /new  /clear  /global  /history  /help
//
// Q8 — dynamic skill-pack fold-in: deferred until `GET /api/assistant/
// skill-packs` lands; the palette filters built-ins only for now. When
// that endpoint ships, push the pulled aliases into BUILT_INS and the
// palette folds them in without further changes.

export type SlashCommandId =
    | "fav"
    | "unfav"
    | "rename"
    | "new"
    | "clear"
    | "global"
    | "history"
    | "help";

export interface SlashCommand {
    id: SlashCommandId;
    label: string;          // including the leading `/`
    hint: string;
    takesArg: boolean;
    aliases?: string[];     // matched against the filter token
}

const BUILT_INS: ReadonlyArray<SlashCommand> = [
    { id: "fav", label: "/fav", hint: "Star this conversation", takesArg: false, aliases: ["star"] },
    { id: "unfav", label: "/unfav", hint: "Remove the star", takesArg: false },
    { id: "rename", label: "/rename", hint: "Rename this conversation", takesArg: true },
    { id: "new", label: "/new", hint: "Start a fresh conversation on this screen", takesArg: false },
    { id: "clear", label: "/clear", hint: "Reset filters / sort / groupby on the active view", takesArg: false },
    { id: "global", label: "/global", hint: "Route the next message through the global tool surface", takesArg: false },
    { id: "history", label: "/history", hint: "Open the conversation history panel", takesArg: false },
    { id: "help", label: "/help", hint: "Show what the assistant can do", takesArg: false },
];

const ICONS: Record<SlashCommandId, typeof Star> = {
    fav: Star,
    unfav: BookmarkX,
    rename: Pencil,
    new: MessagesSquare,
    clear: Brush,
    global: Globe,
    history: HistoryIcon,
    help: HelpCircle,
};

/** Pure: filter the built-in command list by the typed token (text after
    `/` up to whitespace). Empty filter → all commands. Matches id +
    aliases case-insensitively. Exported for tests. */
export function filterCommands(filter: string, source: ReadonlyArray<SlashCommand> = BUILT_INS): SlashCommand[] {
    const f = filter.trim().toLowerCase();
    if (!f) return [...source];
    return source.filter((c) => {
        if (c.id.startsWith(f)) return true;
        if (c.aliases?.some((a) => a.startsWith(f))) return true;
        return false;
    });
}

/** Extract the trailing argument after a `/command ` prefix in the draft.
    For commands with takesArg=true (e.g. `/rename My new title`), the
    parent uses this to read the arg before firing onSelect. */
export function extractArg(currentDraft: string, command: SlashCommand): string | undefined {
    if (!command.takesArg) return undefined;
    const prefix = command.label + " ";
    if (currentDraft.startsWith(prefix)) {
        return currentDraft.slice(prefix.length).trim() || undefined;
    }
    return undefined;
}

interface Props {
    /** The text after the leading `/`, up to whitespace. */
    filter: string;
    /** The full current draft — used to read a trailing argument. */
    currentDraft: string;
    onSelect: (cmd: SlashCommand, arg?: string) => void;
    onClose: () => void;
}

export function SlashPalette({ filter, currentDraft, onSelect, onClose }: Props) {
    const filtered = useMemo(() => filterCommands(filter), [filter]);
    const [highlightedIdx, setHighlightedIdx] = useState(0);
    const rootRef = useRef<HTMLDivElement | null>(null);

    // Reset highlight when filter changes (avoid pointing at a now-hidden row)
    useEffect(() => {
        setHighlightedIdx(0);
    }, [filter]);

    // Keyboard handlers — ArrowUp/Down to navigate, Enter to select, Esc to close.
    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (filtered.length === 0) return;
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightedIdx((i) => (i + 1) % filtered.length);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightedIdx((i) => (i - 1 + filtered.length) % filtered.length);
            } else if (e.key === "Enter") {
                e.preventDefault();
                const cmd = filtered[highlightedIdx];
                if (cmd) onSelect(cmd, extractArg(currentDraft, cmd));
            } else if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [filtered, highlightedIdx, currentDraft, onSelect, onClose]);

    if (filtered.length === 0) {
        return (
            <div className="assistant-slash-palette" ref={rootRef} data-testid="assistant-slash-palette">
                <div className="assistant-slash-palette__empty">No matching commands</div>
            </div>
        );
    }

    return (
        <div className="assistant-slash-palette" ref={rootRef} data-testid="assistant-slash-palette">
            {filtered.map((cmd, i) => {
                const Icon = ICONS[cmd.id];
                const isHighlighted = i === highlightedIdx;
                return (
                    <button
                        key={cmd.id}
                        type="button"
                        className={`assistant-slash-palette__item${isHighlighted ? " assistant-slash-palette__item--highlighted" : ""}`}
                        data-testid={`slash-${cmd.id}`}
                        onMouseEnter={() => setHighlightedIdx(i)}
                        onClick={() => onSelect(cmd, extractArg(currentDraft, cmd))}
                    >
                        <Icon size={12} className="assistant-slash-palette__item-icon" />
                        <span className="assistant-slash-palette__item-label">{cmd.label}</span>
                        <span className="assistant-slash-palette__item-hint">{cmd.hint}</span>
                    </button>
                );
            })}
        </div>
    );
}
